import { cached, tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { getOwner, setOwner } from "@ember/owner";
import { service } from "@ember/service";
import { isSkinTonableEmoji } from "pretty-text/emoji";
import EmojiPickerDetached from "discourse/components/emoji-picker/detached";
import AnonymousFlagModal from "discourse/components/modal/anonymous-flag";
import BookmarkModal from "discourse/components/modal/bookmark";
import FlagModal from "discourse/components/modal/flag";
import ReportIllegalContentModal from "discourse/components/modal/report-illegal-content";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { BookmarkFormData } from "discourse/lib/bookmark-form-data";
import { bind } from "discourse/lib/decorators";
import getURL, { getAbsoluteURL } from "discourse/lib/get-url";
import { clipboardCopy } from "discourse/lib/utilities";
import Bookmark from "discourse/models/bookmark";
import { DEFAULT_DIVERSITY } from "discourse/services/emoji-store";
import { i18n } from "discourse-i18n";
import { MESSAGE_CONTEXT_THREAD } from "discourse/plugins/chat/discourse/components/chat-message";
import ChatMessageFlag from "discourse/plugins/chat/discourse/lib/chat-message-flag";
import ChatMessage from "discourse/plugins/chat/discourse/models/chat-message";
import ChatMessageReactionModel, {
  REACTIONS,
} from "discourse/plugins/chat/discourse/models/chat-message-reaction";

const removedSecondaryActions = new Set();

export function removeChatComposerSecondaryActions(actionIds) {
  actionIds.forEach((id) => removedSecondaryActions.add(id));
}

export function resetRemovedChatComposerSecondaryActions() {
  removedSecondaryActions.clear();
}

export default class ChatMessageInteractor {
  @service appEvents;
  @service chat;
  @service chatChannelComposer;
  @service chatThreadComposer;
  @service chatChannelPane;
  @service chatThreadPane;
  @service chatApi;
  @service currentUser;
  @service site;
  @service modal;
  @service capabilities;
  @service siteSettings;
  @service menu;
  @service toasts;
  @service interactedChatMessage;
  @service emojiStore;

  @tracked message = null;
  @tracked context = null;

  #quickReactionEmojis;
  #placeholderReactions = new Map();

  constructor(owner, message, context) {
    setOwner(this, owner);

    this.message = message;
    this.context = context;
  }

  // Reacting re-sorts the frequency-ordered quick reactions, so the emoji are resolved
  // once and held: the controls must not move under a user who is still interacting
  // with them. An interactor is built per open actions menu, which scopes the freeze.
  get quickReactionEmojis() {
    this.#quickReactionEmojis ??= this.#computeQuickReactionEmojis();
    return this.#quickReactionEmojis;
  }

  @cached
  get emojiReactions() {
    return this.quickReactionEmojis.map(
      (emoji) =>
        this.message.reactions.find((reaction) => reaction.emoji === emoji) ||
        this.#placeholderReaction(emoji)
    );
  }

  get pane() {
    return this.context === MESSAGE_CONTEXT_THREAD
      ? this.chatThreadPane
      : this.chatChannelPane;
  }

  get canEdit() {
    return (
      !this.message.deletedAt &&
      this.currentUser.id === this.message.user.id &&
      this.message.channel?.canModifyMessages?.(this.currentUser)
    );
  }

  get canInteractWithMessage() {
    return (
      this.#canParticipate &&
      !this.message?.deletedAt &&
      this.message?.channel?.canModifyMessages(this.currentUser) &&
      this.message?.channel?.isFollowing
    );
  }

  get canRestoreMessage() {
    return (
      this.message?.deletedAt &&
      (this.currentUser.staff ||
        this.message?.channel?.canModerate ||
        (this.message?.user?.id === this.currentUser.id &&
          this.message?.deletedById === this.currentUser.id)) &&
      this.message.channel?.canModifyMessages?.(this.currentUser)
    );
  }

  get canBookmark() {
    return (
      this.#canParticipate &&
      this.message?.channel?.canModifyMessages?.(this.currentUser)
    );
  }

  get canReply() {
    return (
      this.canInteractWithMessage && this.context !== MESSAGE_CONTEXT_THREAD
    );
  }

  get canReact() {
    // Reactions are emoji, so there's nothing to react with while they're disabled.
    return this.canInteractWithMessage && this.siteSettings.enable_emoji;
  }

  get canReportMessage() {
    return (
      this.canFlagMessage ||
      (this.siteSettings.dsa_reporting_enabled &&
        !this.message?.chatWebhookEvent &&
        !this.message?.deletedAt)
    );
  }

  get canFlagMessage() {
    if (!this.currentUser) {
      return (
        this.siteSettings.allow_all_users_to_flag_illegal_content &&
        !this.message?.chatWebhookEvent &&
        !this.message?.deletedAt
      );
    }

    return (
      this.currentUser.id !== this.message?.user?.id &&
      this.message?.userFlagStatus === undefined &&
      this.message.channel?.canFlag &&
      !this.message?.chatWebhookEvent &&
      !this.message?.deletedAt
    );
  }

  get canPinMessage() {
    if (!this.siteSettings.chat_pinned_messages) {
      return false;
    }
    return this.message.channel?.canManagePins;
  }

  get canRebakeMessage() {
    return (
      this.currentUser.staff &&
      this.message.channel?.canModifyMessages?.(this.currentUser)
    );
  }

  get canDeleteMessage() {
    return (
      this.canDelete &&
      !this.message?.deletedAt &&
      this.message.channel?.canModifyMessages?.(this.currentUser)
    );
  }

  get canDelete() {
    return this.currentUser.id === this.message.user.id
      ? this.message.channel?.canDeleteSelf
      : this.message.channel?.canDeleteOthers;
  }

  get composer() {
    return this.context === MESSAGE_CONTEXT_THREAD
      ? this.chatThreadComposer
      : this.chatChannelComposer;
  }

  get secondaryActions() {
    if (!this.#canParticipate) {
      return this.#readOnlySecondaryActions;
    }

    const buttons = [];

    buttons.push({
      id: "copyLink",
      name: i18n("chat.copy_link"),
      icon: "link",
    });

    if (this.site.mobileView) {
      buttons.push({
        id: "copyText",
        name: i18n("chat.copy_text"),
        icon: "clipboard",
      });
    }

    if (this.canEdit) {
      buttons.push({
        id: "edit",
        name: i18n("chat.edit"),
        icon: "pencil",
      });
    }

    if (!this.pane.selectingMessages) {
      buttons.push({
        id: "select",
        name: i18n("chat.select"),
        icon: "list-check",
      });
    }

    if (this.canPinMessage && !this.message.pinned) {
      buttons.push({
        id: "pin",
        name: i18n("chat.pin_message"),
        icon: "thumbtack",
      });
    }

    if (this.canPinMessage && this.message.pinned) {
      buttons.push({
        id: "unpin",
        name: i18n("chat.unpin_message"),
        icon: "thumbtack",
      });
    }

    if (this.canReportMessage) {
      buttons.push({
        id: "flag",
        name: i18n("chat.flag"),
        icon: "flag",
      });
    }

    if (this.canDeleteMessage) {
      buttons.push({
        id: "delete",
        name: i18n("chat.delete"),
        icon: "trash-can",
      });
    }

    if (this.canRestoreMessage) {
      buttons.push({
        id: "restore",
        name: i18n("chat.restore"),
        icon: "arrow-rotate-left",
      });
    }

    if (this.canRebakeMessage) {
      buttons.push({
        id: "rebake",
        name: i18n("chat.rebake_message"),
        icon: "rotate",
      });
    }

    return buttons.filter((button) => !removedSecondaryActions.has(button.id));
  }

  // Visitors and silenced users can't post, but can still link to and report messages.
  get #canParticipate() {
    return this.currentUser && this.chat.userCanInteractWithChat;
  }

  get #readOnlySecondaryActions() {
    const buttons = [
      { id: "copyLink", name: i18n("chat.copy_link"), icon: "link" },
    ];

    if (this.site.mobileView) {
      buttons.push({
        id: "copyText",
        name: i18n("chat.copy_text"),
        icon: "clipboard",
      });
    }

    if (this.canReportMessage) {
      buttons.push({ id: "flag", name: i18n("chat.flag"), icon: "flag" });
    }

    return buttons.filter((button) => !removedSecondaryActions.has(button.id));
  }

  select(checked = true) {
    this.message.selected = checked;
    this.pane.onSelectMessage(this.message);
  }

  bulkSelect(checked) {
    const manager = this.message.manager;
    const lastSelectedIndex = manager.findIndexOfMessage(
      this.pane.lastSelectedMessage.id
    );
    const newlySelectedIndex = manager.findIndexOfMessage(this.message.id);
    const sortedIndices = [lastSelectedIndex, newlySelectedIndex].sort(
      (a, b) => a - b
    );

    for (let i = sortedIndices[0]; i <= sortedIndices[1]; i++) {
      manager.messages[i].selected = checked;
    }
  }

  copyText() {
    clipboardCopy(this.message.message);
    this.toasts.success({
      duration: "short",
      data: { message: i18n("chat.text_copied") },
    });
  }

  copyLink() {
    const { protocol, host } = window.location;
    const channelId = this.message.channel.id;
    const threadId = this.message.thread?.id;

    let url;
    if (this.context === MESSAGE_CONTEXT_THREAD && threadId) {
      url = getURL(`/chat/c/-/${channelId}/t/${threadId}/${this.message.id}`);
    } else {
      url = getURL(`/chat/c/-/${channelId}/${this.message.id}`);
    }

    url = url.indexOf("/") === 0 ? protocol + "//" + host + url : url;
    clipboardCopy(url);
    this.toasts.success({
      duration: "short",
      data: { message: i18n("chat.link_copied") },
    });
  }

  @action
  react(emoji, reactAction) {
    if (!this.chat.userCanInteractWithChat) {
      return;
    }

    if (this.pane.reacting) {
      return;
    }

    if (this.capabilities.userHasBeenActive && this.capabilities.canVibrate) {
      navigator.vibrate(5);
    }

    if (this.site.mobileView) {
      this.chat.activeMessage = null;
    }

    this.pane.reacting = true;

    this.message.react(
      emoji,
      reactAction,
      this.currentUser,
      this.currentUser.id
    );

    return this.chatApi
      .publishReaction(
        this.message.channel.id,
        this.message.id,
        emoji,
        reactAction
      )
      .then(() => {
        this.emojiStore.trackEmojiForContext(emoji, "chat");
      })
      .catch((errResult) => {
        popupAjaxError(errResult);
        this.message.react(
          emoji,
          REACTIONS.remove,
          this.currentUser,
          this.currentUser.id
        );
      })
      .finally(() => {
        this.pane.reacting = false;
      });
  }

  @action
  toggleBookmark() {
    // somehow, this works around a low-level chrome rendering issue which
    // causes a complete browser crash when saving/deleting bookmarks in chat.
    // Error message: "Check failed: !NeedsToUpdateCachedValues()."
    // Internal topic: t/143485
    // Hopefully, this can be dropped in future chrome versions
    document.activeElement?.blur();

    this.modal.show(BookmarkModal, {
      model: {
        bookmark: new BookmarkFormData(
          this.message.bookmark ||
            Bookmark.createFor(
              this.currentUser,
              "Chat::Message",
              this.message.id
            )
        ),
        afterSave: (bookmarkFormData) => {
          const bookmark = Bookmark.create(bookmarkFormData.saveData);
          this.message.bookmark = bookmark;
          this.appEvents.trigger(
            "bookmarks:changed",
            bookmarkFormData.saveData,
            bookmark.attachedTo()
          );
        },
        afterDelete: () => {
          this.message.bookmark = null;
        },
      },
    });
  }

  @action
  flag() {
    if (!this.currentUser && this.siteSettings.dsa_reporting_enabled) {
      this.modal.show(ReportIllegalContentModal, {
        model: { contentUrl: getAbsoluteURL(this.message.url) },
      });
      return;
    }

    const model = new ChatMessage(this.message.channel, this.message);
    if (!this.canFlagMessage) {
      model.availableFlags = [];
    }
    model.username = this.message.user?.username;
    model.user_id = this.message.user?.id;
    this.modal.show(this.currentUser ? FlagModal : AnonymousFlagModal, {
      model: {
        allowIllegalContentReport: true,
        flagTarget: new ChatMessageFlag(getOwner(this)),
        flagModel: model,
        setHidden: () => model.set("hidden", true),
      },
    });
  }

  @action
  delete() {
    return this.chatApi
      .trashMessage(this.message.channel.id, this.message.id)
      .catch(popupAjaxError);
  }

  @action
  restore() {
    return this.chatApi
      .restoreMessage(this.message.channel.id, this.message.id)
      .catch(popupAjaxError);
  }

  @action
  rebake() {
    return this.chatApi
      .rebakeMessage(this.message.channel.id, this.message.id)
      .catch(popupAjaxError);
  }

  @action
  pin() {
    this.message.pinned = true;
    this.message.channel.pinnedMessagesCount++;
    this.message.channel.pendingOptimisticPins.add(this.message.id);

    return this.chatApi
      .pinMessage(this.message.channel.id, this.message.id)
      .catch((error) => {
        this.message.pinned = false;
        this.message.channel.pinnedMessagesCount--;
        this.message.channel.pendingOptimisticPins.delete(this.message.id);
        popupAjaxError(error);
      });
  }

  @action
  unpin() {
    this.message.pinned = false;
    this.message.channel.pinnedMessagesCount = Math.max(
      0,
      this.message.channel.pinnedMessagesCount - 1
    );
    this.message.channel.pendingOptimisticUnpins.add(this.message.id);

    return this.chatApi
      .unpinMessage(this.message.channel.id, this.message.id)
      .catch((error) => {
        this.message.pinned = true;
        this.message.channel.pinnedMessagesCount++;
        this.message.channel.pendingOptimisticUnpins.delete(this.message.id);
        popupAjaxError(error);
      });
  }

  @action
  reply() {
    this.composer.replyTo(this.message);
  }

  @action
  edit() {
    this.composer.edit(this.message);
  }

  @action
  async openEmojiPicker(trigger) {
    this.interactedChatMessage.emojiPickerOpen = true;

    await this.menu.show(trigger, {
      identifier: "emoji-picker",
      groupIdentifier: "emoji-picker",
      component: EmojiPickerDetached,
      onClose: () => {
        this.interactedChatMessage.emojiPickerOpen = false;
      },
      data: {
        context: "chat",
        didSelectEmoji: (emoji) => {
          this.selectReaction(emoji);
        },
      },
    });
  }

  @action
  async closeEmojiPicker() {
    await this.menu.close("emoji-picker");
    this.interactedChatMessage.emojiPickerOpen = false;
  }

  @bind
  selectReaction(emoji) {
    if (!this.chat.userCanInteractWithChat) {
      return;
    }

    this.react(emoji, REACTIONS.add);
  }

  @action
  handleSecondaryActions(id) {
    this[id](this.message);
  }

  #computeQuickReactionEmojis() {
    const userQuickReactionsCustom = (
      (this.currentUser.user_option.chat_quick_reaction_type === "custom" &&
        this.currentUser.user_option.chat_quick_reactions_custom) ||
      ""
    )
      .split("|")
      .filter(Boolean);

    const frequentReactions = this.emojiStore.favoritesForContext("chat");

    const defaultReactions = this.siteSettings.default_emoji_reactions
      .split("|")
      .map((emoji) => {
        if (
          this.emojiStore.diversity !== DEFAULT_DIVERSITY &&
          isSkinTonableEmoji(emoji)
        ) {
          return `${emoji}:t${this.emojiStore.diversity}`;
        }

        return emoji;
      });

    const allReactionsInOrder = userQuickReactionsCustom
      .concat(frequentReactions)
      .concat(defaultReactions);

    return allReactionsInOrder
      .filter((item, index) => {
        return allReactionsInOrder.indexOf(item) === index;
      })
      .filter(Boolean)
      .slice(0, 3);
  }

  // An emoji with no reaction yet still needs a model to render against, and it has to
  // keep its identity across re-renders or the control it backs is torn down.
  #placeholderReaction(emoji) {
    let reaction = this.#placeholderReactions.get(emoji);

    if (!reaction) {
      reaction = ChatMessageReactionModel.create({ emoji });
      this.#placeholderReactions.set(emoji, reaction);
    }

    return reaction;
  }
}
